import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import WindowBorder from './WindowBorder';
import Taskbar from './Taskbar';
import ContextMenu from './ContextMenu';
import MenuBar from './MenuBar';
import * as HandleFunctions from './includes/WindowManagerActions';
import { reducer, initialState } from './includes/WindowManagerReducer';
import { loadWindowState, saveWindowState, initializeWindows } from './includes/StateFunctions';
import { useIsolatedRef, useIsolatedReducer } from './includes/customHooks';
import { getPrograms } from './programs';
import { useWindowData } from './includes/WindowContext';
import './styles/scrollbar.css';

import { createNewWindow, convertObjectFunctions } from './includes/WindowManagerFunctions';
import { getIndexedTitle } from './includes/utils';
import {
	WINBIT32_OPEN_WINDOW_EVENT,
	requestOpenWindow,
} from './openWindowEvent';
import {
	clearVaultScope,
	unregisterWindowParent,
} from '../../services/vultisig.js';
import { createLogger } from '../../utils/logger.js';
import _, { set } from 'lodash';
import { e } from 'mathjs';

const log = createLogger('WindowManager');

const WindowManager = ({ programs, windowName, windowId, windowA, handleOpenFunction, setStateAndSave, providerKey, setWindowMenu, programData, 
	// `onOpenWindow` defaults to null, not a no-op: it is only ever forwarded on
	// as `parentOnOpenWindow`, and children (ProgGroup, FunctionsApp) branch on
	// whether an outer manager actually exists. A no-op looks like one and
	// swallows the click.
	setProgramData, handleOpenArray, handleExit, appData = {}, hashPath = [], sendUpHash = () => {}, inContainer, onOpenWindow = null }) => {

	const updatedState = {
		windowName,
		...initialState
	};

	const [state, dispatch, getState] = useIsolatedReducer(windowName, 'windowManagerState', reducer, updatedState);
	const { windows, minimizedWindows, contextMenuVisible, contextMenuPosition, highestZIndex, windowHistory, programList, closedWindows } = state;

	/** Drives compact toolbar in WindowContainer (win3.css) — more reliable than :has(.window-border). */
	const openWindowCount = useMemo(() => {
		if (!Array.isArray(windows)) return 0;
		return windows.filter(w => !closedWindows.includes(w.windowId)).length;
	}, [windows, closedWindows]);

	const { embedMode, setShowScreensaver, setShowMatrix } = appData;
	const triggerScreensaver = setShowScreensaver || setShowMatrix;

	const contextWindowId = useIsolatedRef(windowName, 'contextWindowId', null);
	const defaultProgramsInitialized = useIsolatedRef(windowName, 'defaultProgramsInitialized', false);

	const highestZIndexRef = useRef(highestZIndex);
	const { setWindowContent } = useWindowData();

	const hashPathRef = useRef(hashPath);
	
	const downstreamHashes = useRef({});
	const [hashUpdateTrigger, setHashUpdateTrigger] = React.useState(0);
	const sendUpHashRef = useRef(sendUpHash);
	const lastSentHashRef = useRef('');
	const frontWindowRef = useRef(null);

	

	useEffect(() => {
		if (highestZIndex !== highestZIndexRef.current) highestZIndexRef.current = highestZIndex;
	}, [highestZIndex]);

	// Keep a stable reference to the upstream sendUpHash to avoid effect churn
	useEffect(() => {
		sendUpHashRef.current = sendUpHash;
	}, [sendUpHash]);

	const windowsRef = useRef(windows);
	useEffect(() => { windowsRef.current = windows; }, [windows]);

	// When the launcher itself goes away (Privacy Suite / Money
	// Manager closed, page reloaded, …) we must release every
	// sub-window vault-scope this manager owns. Without this the
	// scopes leak in module-level state and the next time the user
	// re-opens the program they inherit the previous session's
	// loaded vault — the exact "IT'S STILL THERE!" complaint.
	useEffect(() => {
		return () => {
			const tracked = windowsRef.current || [];
			for (const w of tracked) {
				try {
					if (w?.windowId) {
						clearVaultScope(w.windowId);
						unregisterWindowParent(w.windowId);
					}
				} catch (err) {
					log.warn('WindowManager unmount: scope cleanup failed:',
						err?.message || err);
				}
			}
		};
	}, []);

	const programDataRef = useRef(programData);
	useEffect(() => { programDataRef.current = programData; }, [programData]);

	const closeWindow = useCallback((window) => {
		log.debug('closeWindow called with window:', window);
		if (window.unCloseable) return;
		dispatch({ type: 'CLOSE_WINDOW', payload: window.windowId });
		saveWindowState(windowName, windowsRef.current.filter(w => w.windowId !== window.windowId));
		setWindowContent(window.windowId, {});
		// Release the closed window's vault-scope slot so a future
		// program with a fresh windowId never inherits its loaded
		// vault. Without this, `_findActiveVaultAnyScope` would keep
		// returning the previous vault even after every visible
		// surface was closed (see the
		// "I closed both wizards and it's STILL THERE" report).
		try {
			clearVaultScope(window.windowId);
			unregisterWindowParent(window.windowId);
		} catch (err) {
			log.warn('closeWindow: vault scope cleanup failed:', err?.message || err);
		}
	}, [windowName, setWindowContent, dispatch]);

	const handleOpenWindow = useCallback((program, metadata, saveState = true) => {
		if (!program) {
			log.error("handleOpenWindow: program is undefined or null");
			return;
		}
		return createNewWindow(programs, windowName, handleOpenArray, programDataRef.current, highestZIndexRef, (newIndex) => dispatch({ type: 'SET_HIGHEST_Z_INDEX', payload: newIndex }), dispatch, closeWindow, program, metadata, saveState);
	}, [programs, windowName, handleOpenArray, dispatch, closeWindow, highestZIndexRef]);

	const openHelpForWindow = useCallback((progName, topic) => {
		const allProgs = programs || [];
		const findHelp = (list) => {
			for (const p of list) {
				if (p.progName === 'winhelp.exe') return p;
				if (p.programs) {
					const found = findHelp(p.programs);
					if (found) return found;
				}
			}
			return null;
		};
		const helpProg = findHelp(allProgs);
		if (helpProg) {
			handleOpenWindow(
				{ ...helpProg, params: { progName: progName || 'contents', topic: topic || null } },
				{},
				false
			);
			return;
		}
		// Sub-window managers (e.g. the Privacy Suite's inner desktop)
		// don't carry winhelp.exe in their program list, which used to
		// make the titlebar "?" a silent no-op for windows like the
		// Purse or the Bus Station. Bubble the request up to the
		// desktop-level manager instead; HelpApp reads the progName
		// from window metadata and routes via PROG_HELP_MAP.
		requestOpenWindow('winhelp.exe', {
			progName: progName || 'contents',
			topic: topic || null,
		});
	}, [programs, handleOpenWindow]);

	useEffect(() => {
		window.document.body.style.cursor = 'default';
		window.document.body.classList.remove('wait');
		window.waitingFor = null;

		if (setWindowMenu) {
			const windowMenu = windows.map(window => {
				const indexedTitle = getIndexedTitle(window.title, window.progName, windows, window.windowId);
				return {
					label: indexedTitle,
					fAction: () => bringToFront(window.windowId),
				};
			});
			setWindowMenu(windowMenu);
		}

	}, [windows, setWindowMenu]);

	const handleStateChange = useCallback((windowId, newData) => {
		// Implement the logic for state change
	}, []);

	const handleContextMenu = useCallback((position, windowId) => {
		if (contextMenuVisible) {
			dispatch({ type: 'SET_CONTEXT_MENU_VISIBLE', payload: false });
		} else {
			dispatch({ type: 'SET_CONTEXT_MENU_POSITION', payload: position });
			dispatch({ type: 'SET_CONTEXT_MENU_VISIBLE', payload: true });
			contextWindowId.current = windowId;
		}
	}, [contextMenuVisible, dispatch]);

	const minimizeWindow = useCallback((window) => {
		dispatch({ type: 'MINIMIZE_WINDOW', payload: window });
	}, [dispatch]);

	const getCurrentFrontWindow = useMemo(() => {
		if (!Array.isArray(windows) || windows.length === 0) return null;
		return windows.reduce((a, b) => (a.zIndex > b.zIndex ? a : b));
	}, [windows]);

	const bringToFront = useCallback((windowId, hasChildrenWindows = false) => {
		log.debug('bringToFront called with windowID:', windowId, hasChildrenWindows, downstreamHashes.current[windowId], getCurrentFrontWindow?.id);

		if (getCurrentFrontWindow?.id === windowId) return;
		if (!hasChildrenWindows){
			downstreamHashes.current[windowId] = [];
		}
		dispatch(HandleFunctions.bringToFront(windowId));
	}, [dispatch, getCurrentFrontWindow]);

	const restoreWindow = useCallback((window) => {
		dispatch({ type: 'RESTORE_WINDOW', payload: window });
		bringToFront(window.windowId);
	}, [dispatch, bringToFront]);

	const maximizeWindow = useCallback((window) => {
		dispatch({ type: 'MAXIMIZE_WINDOW', payload: window });
	}, [dispatch]);

	// Track the latest front window in a ref so callbacks see up-to-date value
	useEffect(() => {
		frontWindowRef.current = getCurrentFrontWindow;
	}, [getCurrentFrontWindow]);

	const openWindowByProgName = useCallback((progName, metadata) => {
		createNewWindow(programs, windowName, handleOpenArray, programData, highestZIndexRef, (newIndex) => dispatch({ type: 'SET_HIGHEST_Z_INDEX', payload: newIndex }), dispatch, closeWindow, progName, metadata, true);
	}, [closeWindow, dispatch, handleOpenArray, programData, programs]);

	// Double-clicking bare desktop opens Control Panel → Display, the way
	// double-clicking the Win 3.1 desktop opened the Desktop applet. Only on
	// the desktop manager, and only when the double-click landed on the
	// background itself — inside a window it belongs to that window.
	const handleDesktopDoubleClick = useCallback((event) => {
		if (windowName !== 'desktop') return;
		if (event.target !== event.currentTarget) return;
		openWindowByProgName('display.exe');
	}, [windowName, openWindowByProgName]);

	const handleHashChange = useCallback(() => {
		HandleFunctions.handleHashChange(dispatch, getState)();
	}, [dispatch, getState]);

	const tileWindows = useCallback(() => {
		dispatch({ type: 'TILE_WINDOWS' });
	}, [dispatch]);

	const handleContextMenuAction = useCallback((action) => {
		const window = windows.find(w => w.windowId === contextWindowId.current);
		if (window) {
			switch (action) {
				case 'close':
					closeWindow(window);
					break;
				case 'restore':
					restoreWindow(window);
					break;
				case 'minimize':
					minimizeWindow(window);
					break;
				case 'maximize':
					maximizeWindow(window);
					break;
				case 'tile':
					tileWindows();
					break;
				case 'screensaver':
					if (triggerScreensaver) {
						triggerScreensaver(true);
					}
					break;
				default:
					log.debug('Unknown action');
			}
		}
		dispatch({ type: 'SET_CONTEXT_MENU_VISIBLE', payload: false });
	}, [windows, closeWindow, restoreWindow, minimizeWindow, maximizeWindow, tileWindows, dispatch, triggerScreensaver]);

	const handleMenuClick = useCallback((action, window) => {
		if (window && window.menuHandler) {
			window.menuHandler(action);
		} else {
			log.debug(`Unknown action: ${action}`);
		}
	}, []);

	const functionMap = useMemo(() => ({
		handleOpenWindow,
		handleStateChange,
		handleQRRead: HandleFunctions.handleQRRead(dispatch),
		toggleQRPop: HandleFunctions.toggleQRPop(dispatch),
		handleExit: HandleFunctions.handleExit(dispatch),
		handleMenuClick: HandleFunctions.handleMenuClick(dispatch),
		handleMenuAction: HandleFunctions.handleMenuAction(dispatch),
	}), [handleOpenWindow, dispatch]);

	const handleOpenWindowRef = useRef(handleOpenWindow);
	useEffect(() => { handleOpenWindowRef.current = handleOpenWindow; }, [handleOpenWindow]);

	const stableHandleOpen = useCallback((...args) => handleOpenWindowRef.current?.(...args), []);

	useEffect(() => {
		if (Array.isArray(programs)) {
			programs.forEach(program => convertObjectFunctions(program, functionMap));
		}

		if (handleOpenFunction) {
			handleOpenFunction(stableHandleOpen);
		}
		if (handleOpenArray) {
			handleOpenArray.push(stableHandleOpen);
		}
		return () => {
			if (handleOpenArray) {
				const idx = handleOpenArray.indexOf(stableHandleOpen);
				if (idx !== -1) handleOpenArray.splice(idx, 1);
			}
		};
	}, [handleOpenFunction, stableHandleOpen, programs, functionMap, handleOpenArray]);

	useEffect(() => {
		const initialize = async () => {
			if (!Array.isArray(programs)) return;
			const loadedWindows = loadWindowState(windowName);
			const initialWindows = initializeWindows(programs);

			if (!defaultProgramsInitialized.current) {

				// Will the URL hash open a window at THIS manager level?
				// Decided up front so `defaultOpenWhenEmpty` programs know
				// whether something else is already on its way.
				const hashWillOpen = Boolean(
					hashPathRef.current.length && (!windowA || !windowA.invisibleToHash)
				);

				if (!windows || windows.length === 0){
					if (loadedWindows) {
						dispatch({ type: 'SET_WINDOWS', payload: Array.isArray(loadedWindows) ? loadedWindows : [] });
					} else {
						dispatch({ type: 'SET_WINDOWS', payload: initialWindows.windows });
						dispatch({ type: 'SET_HIGHEST_Z_INDEX', payload: initialWindows.highestZIndex });
					}

					const progs = getPrograms();
					const mappedPrograms = progs.map(program => ({
						...program,
						onStateChange: program.onStateChange ? handleStateChange : undefined,
					}));

					programs.forEach(program => convertObjectFunctions(program, functionMap));
					dispatch({ type: 'SET_PROGRAM_LIST', payload: mappedPrograms });
					dispatch({ type: 'SET_PROGRAMS', payload: programs });

					const defaultPrograms = programs.filter(program => program.defaultOpen);
					defaultPrograms.forEach(program => handleOpenWindow(program, {}, false));
					log.debug('Default programs initialized:', defaultPrograms);

					// Fallback openers (e.g. Money Manager's Functions
					// launcher): only when nothing else is opening — no
					// restored session, no defaultOpen program, and no
					// #hash route into this container.
					if (!loadedWindows && !defaultPrograms.length && !hashWillOpen) {
						programs
							.filter(program => program.defaultOpenWhenEmpty)
							.forEach(program => handleOpenWindow(program, {}, false));
					}
				}

				log.debug('hashPath:', hashPath, hashPathRef.current, windowA);

				if (hashPathRef.current.length && (!windowA || !windowA.invisibleToHash)){
					//Pop the first element from the array and open the window
					const progName = hashPathRef.current.shift().split('~');
					log.debug('progName:', progName);
					if(progName.length > 1){
						//open the window with the metadata
						let metadata;
						try {
							metadata = JSON.parse(atob(progName[1]));
						} catch {
							log.warn('Failed to parse window metadata from URL hash');
							openWindowByProgName(progName[0]);
							return;
						}
						openWindowByProgName(progName[0], metadata);


					}else{
						openWindowByProgName(progName[0]);
					}
				}

				defaultProgramsInitialized.current = true;

			}
		};

		initialize();
		// if (windowName === 'desktop') {
		// 	window.addEventListener("hashchange", handleHashChange);

		// 	return () => {
		// 		window.removeEventListener("hashchange", handleHashChange);
		// 	};
		// }
	}, [windowName, programs, handleOpenWindow, handleHashChange, handleStateChange, functionMap, openWindowByProgName]);

	// Desktop-level listener for `requestOpenWindow(...)` from
	// anywhere in the React tree. Replaces the historical pattern
	// of poking `window.location.hash` — that only fired on initial
	// mount because the `hashchange` listener has been disabled
	// since forever. Bound at the DESKTOP `WindowManager` because
	// (a) nested managers only know their own subset of programs,
	// (b) the desktop owns the URL hash so it can mirror back if
	// needed, and (c) we want exactly one handler — multiple
	// managers listening would double-open the same window.
	useEffect(() => {
		if (windowName !== 'desktop') return undefined;
		const handle = (event) => {
			const detail = event && event.detail;
			if (!detail || !detail.progName) return;
			openWindowByProgName(detail.progName, detail.metadata);
		};
		window.addEventListener(WINBIT32_OPEN_WINDOW_EVENT, handle);
		return () => {
			window.removeEventListener(WINBIT32_OPEN_WINDOW_EVENT, handle);
		};
	}, [windowName, openWindowByProgName]);

	useEffect(() => {
		const handleF1 = (e) => {
			if (e.key === 'F1') {
				e.preventDefault();
				const front = windows.find(w => w.id === getCurrentFrontWindow?.id);
				openHelpForWindow(front?.progName);
			}
		};
		document.addEventListener('keydown', handleF1);
		return () => document.removeEventListener('keydown', handleF1);
	}, [windows, getCurrentFrontWindow, openHelpForWindow]);

	// useEffect(() => {
	// 	if(hashPath && hashPath.length && defaultProgramsInitialized.current){
	// 		//Pop the first element from the array and open the window
	// 		const progName = hashPath.shift();
	// 		openWindowByProgName(progName);
	// 		hashPathRef.current = hashPath;
	// 	}
	// }, [hashPath, openWindowByProgName]);	

	// if (window.location.hash !== newHash) {
	// 	window.history.replaceState(null, null, newHash);
	// }


		//receive hash from child window, add to hashParts and send up
	const _sendUpHash = React.useCallback((hash, _windowId) => {
		const frontWindow = frontWindowRef.current;

		if (!frontWindow) {
			log.debug('No front window, sending up hash:', hash, windowA);
			// If no front window and we're invisible to hash, don't propagate
			if (windowA?.invisibleToHash) {
				return;
			}
			// If we have a windowA but no front window, send just our program name
			if (windowA) {
				const payload = [windowA.hashName || windowA.progName.replace(/\.exe$/i, '') || windowA.progName];
				const signature = JSON.stringify(payload);
				if (signature !== lastSentHashRef.current) {
					lastSentHashRef.current = signature;
					sendUpHashRef.current(payload, windowId);
				}
			}
			return;
		}

		// Store the incoming hash from the child window
		if (_windowId) {
			downstreamHashes.current[_windowId] = hash.slice();
			// Trigger re-evaluation of hash effect
			setHashUpdateTrigger(prev => prev + 1);
		}
		if(frontWindow && frontWindow.id === windowId){

			// Get the hashes from our current front window
			const _hash = hash.slice();

			// Only add our window's program name if it's visible to hash
			if (!frontWindow.invisibleToHash) {
				_hash.push(frontWindow.hashName || frontWindow.progName.replace('.exe', ''));
			}

			// Don't propagate if we hit the program manager
			if (!_hash.includes('progman')) {
				const signature = JSON.stringify(_hash);
				if (signature !== lastSentHashRef.current) {
					lastSentHashRef.current = signature;
					sendUpHashRef.current(_hash, windowId);
				}
			}
		}
	}, [windowId]);

	useEffect(() => {
		const frontWindow = getCurrentFrontWindow;
		if (!frontWindow) {
			return;
		}

		// Get stored hashes from the current front window
		const hashes = downstreamHashes.current[frontWindow.id]?.slice() || [];
		
		// Only send up the chain if this window is visible to hash
		let payload;
		if (!frontWindow.invisibleToHash) {
			payload = [...hashes];
			payload.push(frontWindow.hashName || frontWindow.progName.replace('.exe', ''));
		} else {
			payload = hashes;
		}

		const signature = JSON.stringify(payload);
		if (signature !== lastSentHashRef.current) {
			lastSentHashRef.current = signature;
			sendUpHashRef.current(payload, windowId);
		}
	}, [getCurrentFrontWindow, windowId, hashUpdateTrigger]);



	return (
		<>
			<div
				className={`window-manager${openWindowCount > 0 ? ' has-open-windows' : ''}`}
				onDoubleClick={handleDesktopDoubleClick}
			>
				{Array.isArray(windows) && [...windows]
					.sort((a, b) => b.zIndex - a.zIndex) // Changed sort order to show highest zIndex first
					.map(window => {
					if (closedWindows.includes(window.windowId)) {
						return null; // Skip rendering closed windows
					}
					const _windowId = windowName + '_' + window.windowId;

					let title = window.title;
					if(embedMode){
						
						if (window.embededTitle){
							title = window.embededTitle;
						}
						//if downstreamHashes.current[window.windowId] is array, copy and pop the end off

						if (downstreamHashes.current[window.windowId] && downstreamHashes.current[window.windowId].length){
							const frontChildWindow = downstreamHashes.current[window.windowId].slice().pop();
							if (frontChildWindow){
								//remove the .exe and capitalise first letter
								title = title + ' - ' + frontChildWindow.charAt(0).toUpperCase() + frontChildWindow.slice(1).replace('.exe', '')
							}
						}
					}
					
					// Add superscript index if multiple instances exist
					title = getIndexedTitle(title, window.progName, windows, window.windowId);

					return (
						<WindowBorder
							{...window}

							key={_windowId}
							windowId={_windowId}
							title={title}
							onMinimize={() => minimizeWindow(window)}
							onMaximize={(isMaximized) => {
								if (isMaximized) {
									maximizeWindow(window);
								} else {
									restoreWindow(window);
								}
							}}
						onClose={() => closeWindow(window)}
						onHelp={() => openHelpForWindow(window.progName)}
						onClick={(e) => bringToFront(window.windowId, e)}
						onDoubleClick={(e) => {
							e.stopPropagation();
							bringToFront(window.windowId);
						}}
						onContextMenu={(position) => handleContextMenu(position, window.windowId)}
							minimised={window.minimized}
							maximised={window.maximized}
							inContainer={inContainer}
							initialPosition={window.initialPosition}
							zIndex={window.zIndex}
							appData={appData}
							isActiveWindow={getCurrentFrontWindow?.id === window.id}
							metadata={window.metadata}
							windowA={window}
						>
							{window.menu && (
								<MenuBar
									menu={window.menu}
									window={window}
									onMenuClick={(action) => handleMenuClick(action, window)}
								/>
							)}
							{window.component && (
								<>
									{window.component.menu && (
										<MenuBar
											menu={window.component.menu}
											window={window}
											onMenuClick={(action) => handleMenuClick(action, window)}
										/>
									)}
									<div className="window-content">
										<window.component
											onClick={(e) => bringToFront(window.windowId, e)}

											key={windowName + '_component_' + _windowId}
											windowId={window.windowId}
											windowName={window.progName.replace('.exe', '') + '-' + _windowId}
											onStateChange={newData => handleStateChange(window.windowId, newData)}
											initialSubWindows={window.subWindows}
											data={window.data}
											metadata={window.metadata || {}}
											onMenuAction={(menu, window, menuHandler) => functionMap.handleMenuAction(menu, window, menuHandler)}
											// `windowA` is the host-window control surface for
											// the rendered component. Includes self-control
											// (`close` / `minimize` / `maximize` / `isMaximized`)
											// so any component that draws its own TitleBar (the
											// Zcash wizards do, the WindowBorder titlebar is
											// hidden by `.window:has(.zcash-transfer-wizard) >
											// .title { display: none }`) can wire its glyphs
											// directly to the WindowManager. `parentMinimize`
											// is forwarded down from the OUTER WindowManager
											// (via `windowA?.minimize` on this manager itself);
											// when present, sub-windows can opt to minimise
											// their host workspace instead of just themselves.
											windowA={{
												...window,
												close: () => closeWindow(window),
												minimize: () => minimizeWindow(window),
												maximize: (toMax) => {
													if (toMax) {
														maximizeWindow(window);
													} else {
														restoreWindow(window);
													}
												},
												isMaximized: !!window.maximized,
												parentMinimize: typeof windowA?.minimize === 'function'
													? windowA.minimize
													: undefined,
											}}
											programs={programs}
											params={window.params}
											setStateAndSave={setStateAndSave}
											subPrograms={window.programs}
											onWindowDataChange={newData => handleStateChange(window.windowId, newData)}
											controlComponent={window.controlComponent}
											providerKey={providerKey}
											handleOpenArray={handleOpenArray}
											programData={programData}
											setProgramData={setProgramData}
											appData={appData}
											onOpenWindow={handleOpenWindow}
											parentOnOpenWindow={onOpenWindow}
											handleExit={handleExit}
											hashPath={hashPathRef.current}
											sendUpHash={_sendUpHash}

										/>
									</div>
								</>
							)}
							{window.content && <div>{window.content}</div>}
						</WindowBorder>
					);
				})}

				<Taskbar
					minimizedWindows={minimizedWindows}
					onRestore={restoreWindow}
					allWindows={windows}
				/>
			</div>
			{contextMenuVisible && (
				<ContextMenu
					menuItems={[
						{ label: 'Close', shortcut: 'O' },
						{ label: 'Restore', shortcut: 'R' },
						{ label: 'Minimize', shortcut: 'N' },
						{ label: 'Maximize', shortcut: 'X' },
						{ label: 'Tile', shortcut: 'T' },
						{ label: 'Screensaver', shortcut: 'S' },
					]}
					position={contextMenuPosition}
					onAction={handleContextMenuAction}
				/>
			)}
		</>
	);
};

export default React.memo(WindowManager);
